import { computed, toValue, type InjectionKey, type MaybeRef, type Ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { FeedbackTopicType, type FeedbackTopic } from '../../../core/models/feedback-topic'
import { DialogPhase, type Dialog } from '@/core/models/dialog/dialog'

export const useDialogNavigation = (
  feedbackTopics: MaybeRef<ReadonlyArray<FeedbackTopic & { savedAt?: string }>>,
  showObjective: MaybeRef<boolean>, // explicit typing
  dialogPhase: DialogPhase,
  dialogRole: DialogRole,
) => {
  const router = useRouter()
  const route = useRoute()

  const slides = computed<Slide[]>(() => {
    const topics = toValue(feedbackTopics)

    return [
      {
        id: 'start-slide',
        title: 'Übersicht',
        icon: 'play_circle',
        type: DialogSlideType.StartSlide,
      },
      ...topics.map((topic) => ({
        id: topic.id,
        title: topic.title,
        icon: topic.icon,
        type: TOPIC_TO_SLIDE_TYPE[topic.type] ?? DialogSlideType.JournalFeedbackTopic,
        savedAt: topic.savedAt,
        finished:
          dialogPhase !== DialogPhase.Feedback ||
          topic.savedAt != undefined ||
          topic.type == FeedbackTopicType.Journal,
      })),
      ...(toValue(showObjective)
        ? [
            {
              id: 'objective-slide',
              title: 'Ziele',
              icon: 'target',
              type: DialogSlideType.ObjectiveSlide,
            },
          ]
        : []),
    ]
  })

  const currentSlideIndex = computed(() => {
    const currentId = route.params.feedbackTopicId as string
    const currentRouteName = route.name as string

    if (currentRouteName === getRouteName(DialogSlideType.StartSlide)) return 0
    if (currentRouteName === getRouteName(DialogSlideType.ObjectiveSlide))
      return slides.value.findIndex((s) => s.type === DialogSlideType.ObjectiveSlide) ?? 0

    const index = slides.value.findIndex((s) => s.id === currentId)

    return index !== -1 ? index : 0
  })

  const currentSlide = computed(() => slides.value[currentSlideIndex.value])

  const navigateToSlide = (slide: Slide) => {
    const routeName = getRouteName(slide.type)
    if (!routeName) {
      console.warn(`No route defined for slide type: ${slide.type}`)
      return
    }

    const params = slide.id !== 'start-slide' ? { feedbackTopicId: slide.id } : {}
    router.push({ name: routeName, params })
  }

  const getRouteName = (slideType: DialogSlideType) => {
    if (dialogPhase === DialogPhase.Feedback && dialogRole === 'coachee') {
      if (slideType === DialogSlideType.EvluationFeedbackTopic) {
        return 'prepare-evaluation-slide'
      }
      if (slideType === DialogSlideType.PulseFeedbackTopic) {
        return 'prepare-pulse-slide'
      }
    }
    return SLIDE_TYPE_TO_FINAL_ROUTE[slideType]
    // return dialogPhase === DialogPhase.Feedback
    //   ? SLIDE_TYPE_TO_PREPARE_ROUTE[slideType]
    //   : SLIDE_TYPE_TO_FINAL_ROUTE[slideType]
  }

  const changeSlide = (step: number) => {
    const nextIndex = currentSlideIndex.value + step
    if (nextIndex >= 0 && nextIndex < slides.value.length) {
      navigateToSlide(slides.value[nextIndex])
    }
  }

  const hasPreviousSlide = computed(() => currentSlideIndex.value > 0)
  const hasNextSlide = computed(() => currentSlideIndex.value < slides.value.length - 1)

  return {
    slides,
    currentSlide,
    currentSlideIndex,
    selectSlide: (index: number) => navigateToSlide(slides.value[index]),
    nextSlide: () => changeSlide(1),
    previousSlide: () => changeSlide(-1),
    hasPreviousSlide,
    hasNextSlide,
  }
}

// ... Keep your Enum and Interface definitions below ...
export enum DialogSlideType {
  JournalFeedbackTopic = 'journal-feedback-topic',
  EvluationFeedbackTopic = 'evaluation-feedback-topic',
  PulseFeedbackTopic = 'pulse-feedback-topic',
  StartSlide = 'start-slide',
  ObjectiveSlide = 'objective-slide',
  SuperpowerSlide = 'superpower-slide',
}

export interface Slide {
  id: string
  title: string
  icon?: string
  type: DialogSlideType
  savedAt?: string
  finished?: boolean
}

const TOPIC_TO_SLIDE_TYPE: Record<FeedbackTopicType, DialogSlideType> = {
  [FeedbackTopicType.Evaluation]: DialogSlideType.EvluationFeedbackTopic,
  [FeedbackTopicType.Pulse]: DialogSlideType.PulseFeedbackTopic,
  [FeedbackTopicType.Superpower]: DialogSlideType.SuperpowerSlide,
  [FeedbackTopicType.Journal]: DialogSlideType.JournalFeedbackTopic,
}

// const SLIDE_TYPE_TO_PREPARE_ROUTE: Partial<Record<DialogSlideType, string>> = {
//   [DialogSlideType.StartSlide]: 'prepare-start-slide',
//   [DialogSlideType.SuperpowerSlide]: 'prepare-superpower-slide',
//   [DialogSlideType.JournalFeedbackTopic]: 'prepare-journal-slide',
//   [DialogSlideType.EvluationFeedbackTopic]: 'prepare-evaluation-slide',
// }

const SLIDE_TYPE_TO_FINAL_ROUTE: Partial<Record<DialogSlideType, string>> = {
  [DialogSlideType.StartSlide]: 'start-slide',
  [DialogSlideType.SuperpowerSlide]: 'superpower-slide',
  [DialogSlideType.JournalFeedbackTopic]: 'journal-slide',
  [DialogSlideType.EvluationFeedbackTopic]: 'evaluation-slide',
  [DialogSlideType.PulseFeedbackTopic]: 'pulse-slide',
  [DialogSlideType.ObjectiveSlide]: 'objectives-slide',
}

export type DialogRole = 'coach' | 'coachee'

export const dialogKey: InjectionKey<Dialog> = Symbol('Dialog')
export const feedbackTopicsKey: InjectionKey<ReadonlyArray<FeedbackTopic>> =
  Symbol('FeedbackTopics')
export const dialogRoleKey: InjectionKey<DialogRole> = Symbol('DialogRole')
export const hideUnsharedCommentsKey: InjectionKey<Ref<boolean>> = Symbol(
  'HideUnsharedComments',
)
